import { ClipboardModule } from '@angular/cdk/clipboard';
import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, QueryList, ViewChildren, effect, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TabsModule } from 'primeng/tabs';
import { ToastModule } from 'primeng/toast';
import { Subject, catchError, forkJoin, of, takeUntil } from 'rxjs';
import { BulletizeNumberedHeadingsPipe } from '../../shared/pipes/bulletize-numbered-headings.pipe';
import { SoapSentenceBreaksPipe } from '../../shared/pipes/soap-sentence-breaks.pipe';
import { SoapNormalizePipe } from '../../shared/pipes/soap-normalize.pipe';
import { SoapToHtmlPipe } from '../../shared/pipes/soap-to-html.pipe';
import { AIAnalysisContent, MedicalTermsContent, TranscriptionContent, TranscriptionDetailService } from '../transcription-details-service';
import { applyChartPatientName } from '../../shared/utils/chart-patient-name.util';
import { removeFindBelowSummaryBoilerplateLine } from '../../shared/utils/patient-instructions-boilerplate.util';
import { hasDistinctTranslation } from '../../shared/utils/transcript-language.util';
import {
  problemsToAssessmentText,
  problemsToPlanText,
  sectionTextsToProblems,
} from '../../shared/utils/soap-assessment-plan.util';
import {
  TranscriptionDetailResponse,
  TranscriptionsService,
  isCheckpointFinalRecordingKey,
  isFailedTranscriptionStatus
} from '../transcriptions.service';

@Component({
  selector: 'app-transcription-detail',
  standalone: true,
  imports: [
    CommonModule,
    HttpClientModule,
    CardModule,
    ButtonModule,
    ProgressSpinnerModule,
    TabsModule,
    ToastModule,
    ClipboardModule,
    BulletizeNumberedHeadingsPipe,
    SoapSentenceBreaksPipe,
    SoapNormalizePipe,
    SoapToHtmlPipe
  ],
  templateUrl: './transcription-detail.component.html',
  styleUrls: ['./transcription-detail.component.scss']
})
export class TranscriptionDetailComponent implements OnInit, AfterViewInit, OnDestroy {
  private destroy$ = new Subject<void>();

  @ViewChildren('soapTextarea') private soapTextareas!: QueryList<ElementRef<HTMLTextAreaElement>>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private transcriptionsService: TranscriptionsService,
    private transcriptionDetailService: TranscriptionDetailService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef
  ) {
    // Keep textarea heights in sync with any reactive content changes.
    effect(() => {
      // Track dependencies
      try {
        this.soapNotes?.();
      } catch {
        // ignore during early init
      }
      this.visitSummaryText();
      this.treatmentPlanSummaryText();
      this.patientInstructionsText();
      this.aiAnalysisContent();

      // Resize after DOM updates.
      setTimeout(() => this.resizeAllSoapTextareas(), 0);
    });
  }

  transcription = signal<TranscriptionDetailResponse | null>(null);
  loading = signal(true);
  isReanalyzing = signal(false);
  fullTranscriptText = signal<string | null>(null);
  translatedTranscriptText = signal<string | null>(null);
  originalTranscriptText = signal<string | null>(null);
  loadingTranscript = signal(false);
  soapNotes!: ReturnType<TranscriptionDetailService['getSoapNotes']>;
  transcriptionContent = signal<TranscriptionContent | null>(null);
  medicalTermsContent = signal<MedicalTermsContent | null>(null);
  aiAnalysisContent = signal<AIAnalysisContent | null>(null);
  loadingAIAnalysis = signal(false);
  hasChanges = false;
  originalSoapNotes: any = null;
  activeTabValue = 'soap-notes'; // Set SOAP Notes as default active tab
  activeTranscriptTab = signal<'translated' | 'original'>('translated');
  private pendingTranscriptLoad = false;

  visitSummaryText = signal<string>('');
  treatmentPlanSummaryText = signal<string>('');
  icdCodes = signal<Array<{ code: string; description: string }>>([]);
  icdCodesText = signal<string>('');
  patientInstructionsText = signal<string>('');
  /** Baselines for change detection and reset (after load / successful save). */
  private originalVisitSummary = '';
  private originalPatientInstructions = '';
  private originalTreatmentPlanSummary = '';
  /** Which SOAP section is in edit mode (textarea visible). null = show formatted view only. */
  editingSoapSection = signal<string | null>(null);

  setEditingSoapSection(section: string | null): void {
    this.editingSoapSection.set(section);
    if (section) {
      setTimeout(() => this.resizeAllSoapTextareas(), 0);
    }
  }

  // Handle tab change
  onTabChange(tab: string): void {
    this.activeTabValue = tab;

    if (tab === 'soap-notes') {
      setTimeout(() => this.resizeAllSoapTextareas(), 0);
      this.acknowledgeSoapNotesViewed();
    }
    
    // Fetch full transcript when Transcription tab is clicked
    if (tab === 'transcription') {
      if (this.transcription()) {
        this.loadFullTranscript();
      } else {
        this.pendingTranscriptLoad = true;
      }
    }
  }

  // Format transcript text for better readability
  formatTranscript(transcript: string): string {
    if (!transcript) return '';
    
    // Simply add line breaks after sentence-ending punctuation
    // This creates natural paragraph breaks without modifying the content
    let formatted = transcript
      .replace(/([.?!])\s+/g, '$1\n')  // Add newline after sentences
      .trim();
    
    // Convert newlines to HTML breaks
    formatted = formatted.replace(/\n/g, '<br>');
    
    return formatted;
  }

  // Derive title from specialty and appointment type
  getTranscriptionTitle(): string {
    const transcription = this.transcription();
    if (!transcription) return 'Consultation';
    
    if (transcription.specialty) {
      // Format specialty name (e.g., "General Neurology" -> "General Neurology Consultation")
      const specialtyName = transcription.specialty
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
      return `${specialtyName} Consultation`;
    }
    return 'Consultation';
  }

  showAddMoreRecordingAction(): boolean {
    const t = this.transcription();
    if (!t?.originalFileName) {
      return false;
    }
    if (!isCheckpointFinalRecordingKey(t.originalFileName)) {
      return false;
    }
    const st = (t.status ?? '').toLowerCase();
    return st === 'completed' || st === 'needs review' || st === 'failed';
  }

  goAddMoreRecording(): void {
    const t = this.transcription();
    if (!t?.id) {
      return;
    }
    void this.router.navigate(['/v0/recording'], { queryParams: { appendTranscription: t.id } });
  }

  showResumePausedRecordingAction(): boolean {
    const t = this.transcription();
    if (!t?.id || !t.originalFileName) {
      return false;
    }
    const st = (t.status ?? '').toLowerCase();
    if (st !== 'paused') {
      return false;
    }
    return t.originalFileName.trim().toLowerCase().startsWith('paused-recording:');
  }

  goResumePausedRecording(): void {
    const t = this.transcription();
    if (!t?.id || !this.showResumePausedRecordingAction()) {
      return;
    }
    void this.router.navigate(['/v0/recording'], { queryParams: { resumeTranscription: t.id } });
  }

  // Format date for display
  formatDate(dateString: string | undefined): string {
    if (!dateString) return 'N/A';
    try {
      // Parse date string (format: YYYY-MM-DD) as local date to avoid timezone issues
      // Split the date string and create a date object directly to prevent UTC conversion
      const dateParts = dateString.split('-');
      if (dateParts.length === 3) {
        const year = parseInt(dateParts[0], 10);
        const month = parseInt(dateParts[1], 10) - 1; // Month is 0-indexed
        const day = parseInt(dateParts[2], 10);
        const date = new Date(year, month, day);
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const monthName = monthNames[date.getMonth()];
        return `${monthName} ${date.getDate()}, ${date.getFullYear()}`;
      } else {
        // Fallback to original parsing if format is different
        const date = new Date(dateString);
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[date.getMonth()];
        const day = date.getDate();
        const year = date.getFullYear();
        return `${month} ${day}, ${year}`;
      }
    } catch (error) {
      return dateString;
    }
  }

  // Load full transcript content from S3
  private loadFullTranscript(): void {
    const transcription = this.transcription();
    if (!transcription) return;
    
    this.loadingTranscript.set(true);

    forkJoin({
      translated: this.transcriptionsService.getTranscriptContent(transcription.id, 'translated')
        .pipe(catchError(() => of(null))),
      original: this.transcriptionsService.getTranscriptContent(transcription.id, 'original')
        .pipe(catchError(() => of(null)))
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (content) => {
          const translated = content.translated ?? null;
          const original = content.original ?? null;
          this.translatedTranscriptText.set(translated);
          this.originalTranscriptText.set(original);
          this.fullTranscriptText.set(translated ?? original ?? null);
          this.activeTranscriptTab.set(translated ? 'translated' : 'original');
          if (translated == null && original == null) {
            this.messageService.add({
              severity: 'warn',
              summary: 'Transcript unavailable',
              detail: 'Could not load transcript content. It may be processing or unavailable.'
            });
          }
          this.loadingTranscript.set(false);
        },
        error: (err) => {
          console.error('Error loading full transcript:', err);
          this.fullTranscriptText.set(null);
          this.loadingTranscript.set(false);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load transcript content. Please try again.'
          });
        }
      });
  }

  hasTranslatedTranscript(): boolean {
    const transcription = this.transcription();
    return hasDistinctTranslation(
      transcription?.transcriptTranslated || this.translatedTranscriptText(),
      this.originalTranscriptText() || transcription?.transcriptText,
      transcription?.transcriptLanguage
    );
  }

  onTranscriptTabChange(tab: 'translated' | 'original'): void {
    this.activeTranscriptTab.set(tab);
  }

  ngOnInit(): void {
    this.soapNotes = this.transcriptionDetailService.getSoapNotes();
    this.clearDisplayedClinicalContent();

    const transcriptionId = this.route.snapshot.paramMap.get('id');
    if (transcriptionId) {
      this.loadTranscription(transcriptionId);
    } else {
      console.error('No transcription ID found in route');
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No transcription ID provided.'
      });
    }

    setTimeout(() => {
      this.activeTabValue = 'soap-notes';
    }, 100);
  }

  private clearDisplayedClinicalContent(): void {
    this.transcriptionDetailService.clearSoapNotes();
    this.visitSummaryText.set('');
    this.treatmentPlanSummaryText.set('');
    this.patientInstructionsText.set('');
    this.icdCodes.set([]);
    this.icdCodesText.set('');
    this.aiAnalysisContent.set(null);
    this.originalSoapNotes = null;
    this.originalVisitSummary = '';
    this.originalPatientInstructions = '';
    this.originalTreatmentPlanSummary = '';
    this.hasChanges = false;
  }

  private loadAiAnalysisForTranscription(transcriptionId: string): void {
    this.loadingAIAnalysis.set(true);
    this.transcriptionDetailService
      .loadAIAnalysis(transcriptionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (content) => {
          this.aiAnalysisContent.set(content);
          const visit = this.formatBulletLines(content?.keyFindings);
          const treatment = this.formatBulletLines(content?.treatmentPlanSummary);
          this.visitSummaryText.set(visit);
          this.treatmentPlanSummaryText.set(treatment);
          this.originalVisitSummary = visit;
          this.originalTreatmentPlanSummary = treatment;
          const codes = (content?.suggestedICD10Codes ?? []).filter((c) => !!c?.code);
          this.icdCodes.set(codes);
          this.icdCodesText.set(this.formatIcdCodes(codes));
          this.loadingAIAnalysis.set(false);
          this.checkForChanges();
          setTimeout(() => this.resizeAllSoapTextareas(), 0);
        },
        error: (error) => {
          console.error('Error loading AI analysis:', error);
          this.loadingAIAnalysis.set(false);
        }
      });
  }

  private formatBulletLines(items: string[] | null | undefined): string {
    if (!items || items.length === 0) {
      return '';
    }
    return items
      .map(s => (s ?? '').trim())
      .filter(Boolean)
      .map(s => `• ${s}`)
      .join('\n');
  }

  private formatIcdCodes(codes: Array<{ code: string; description: string }>): string {
    if (!codes || codes.length === 0) {
      return '';
    }
    return codes
      .filter(c => !!c?.code)
      .map(c => {
        const code = (c.code ?? '').trim();
        const desc = (c.description ?? '').trim();
        return desc ? `${code} - ${desc}` : code;
      })
      .filter(Boolean)
      .join('\n');
  }

  ngAfterViewInit(): void {
    // Ensure we resize once the textareas exist in the DOM.
    setTimeout(() => this.resizeAllSoapTextareas(), 0);

    // If the view re-renders (e.g., routing, conditional panels), keep sizing correct.
    this.soapTextareas?.changes
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => setTimeout(() => this.resizeAllSoapTextareas(), 0));
  }

  /** Marks SOAP as reviewed for list unread-dot state (no-op if no SOAP). */
  private acknowledgeSoapNotesViewed(): void {
    const t = this.transcription();
    if (!t?.soapNotesAvailable || !t.id) {
      return;
    }
    this.transcriptionsService
      .markSoapNotesRead(t.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.transcriptionsService.requestTranscriptionsListRefresh(),
        error: () => {}
      });
  }

  private loadTranscription(id: string): void {
    this.loading.set(true);
    this.clearDisplayedClinicalContent();

    this.transcriptionsService.getTranscriptionById(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (transcription) => {
          if (transcription) {
            if (isFailedTranscriptionStatus(transcription.status)) {
              this.loading.set(false);
              this.messageService.add({
                severity: 'warn',
                summary: 'Transcription failed',
                detail:
                  transcription.error?.trim() ||
                  'This visit did not complete successfully. Clinical notes are not available.'
              });
              void this.router.navigate(['/v0/transcriptions']);
              return;
            }

            this.transcription.set(transcription);
            this.loading.set(false);

            if (transcription.transcriptText) {
              this.originalTranscriptText.set(transcription.transcriptText);
            }
            if (transcription.transcriptTranslated) {
              this.translatedTranscriptText.set(transcription.transcriptTranslated);
              this.fullTranscriptText.set(transcription.transcriptTranslated);
              this.activeTranscriptTab.set('translated');
            } else if (transcription.transcriptText) {
              this.fullTranscriptText.set(transcription.transcriptText);
              this.activeTranscriptTab.set('original');
            }

            if (this.activeTabValue === 'transcription' || this.pendingTranscriptLoad) {
              this.loadFullTranscript();
            }
            this.pendingTranscriptLoad = false;
            
            // Update SOAP notes with analysis data from the transcription response
            if (transcription.analysis && transcription.analysis.soapNote) {
              const soapNote = transcription.analysis.soapNote;
              const chartName = transcription.patientName;
              const assessmentText = applyChartPatientName(
                this.formatPlanForTextarea(problemsToAssessmentText(soapNote.problems)),
                chartName
              );
              const planText = applyChartPatientName(
                this.formatPlanForTextarea(problemsToPlanText(soapNote.problems)),
                chartName
              );
              const soapData = {
                subjective: applyChartPatientName(soapNote.subjective || '', chartName),
                objective: soapNote.objective || '',
                assessment: assessmentText,
                plan: planText,
              };
              this.transcriptionDetailService.updateSoapNotes(soapData);
              const pi = removeFindBelowSummaryBoilerplateLine(soapNote.patientInstructions || '');
              this.patientInstructionsText.set(pi);
              this.originalPatientInstructions = pi;
              setTimeout(() => this.resizeAllSoapTextareas(), 0);

              this.originalSoapNotes = {
                subjective: soapData.subjective,
                objective: soapNote.objective || '',
                assessment: assessmentText,
                plan: planText,
              };
            } else {
              this.originalSoapNotes = {
                subjective: '',
                objective: '',
                assessment: '',
                plan: ''
              };
              this.patientInstructionsText.set('');
              this.originalPatientInstructions = '';
            }
            this.checkForChanges();

            if (this.activeTabValue === 'soap-notes') {
              this.acknowledgeSoapNotesViewed();
            }

            this.loadAiAnalysisForTranscription(id);
          } else {
            console.error('Transcription not found:', id);
            this.loading.set(false);
            this.messageService.add({
              severity: 'error',
              summary: 'Not Found',
              detail: `Transcription ${id} not found.`
            });
          }
        },
        error: (error) => {
          console.error('TranscriptionDetailComponent: Error loading transcription:', error);
          console.error('TranscriptionDetailComponent: Error status:', error.status);
          console.error('TranscriptionDetailComponent: Error message:', error.message);
          console.error('TranscriptionDetailComponent: Error body:', error.error);
          this.loading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error loading transcription. Please try again.'
          });
        }
      });
  }

  onBack(): void {
    this.router.navigate(['/v0/transcriptions']);
  }

  onResetSoapNotes(): void {
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.removeItem('savedSoapNotes');
    }

    if (this.originalSoapNotes) {
      this.transcriptionDetailService.updateSoapNotes({ ...this.originalSoapNotes });
    }
    this.visitSummaryText.set(this.originalVisitSummary);
    this.patientInstructionsText.set(this.originalPatientInstructions);
    this.treatmentPlanSummaryText.set(this.originalTreatmentPlanSummary);
    this.hasChanges = false;
    this.editingSoapSection.set(null);
    setTimeout(() => this.resizeAllSoapTextareas(), 0);

    this.messageService.add({
      severity: 'info',
      summary: 'Reset',
      detail: 'Notes reset to the last loaded content.'
    });
  }

  onSaveSoapNotes(): void {
    const t = this.transcription();
    if (!t?.id) {
      return;
    }
    const notes = this.soapNotes();
    const payload = {
      subjective: notes.subjective ?? '',
      objective: notes.objective ?? '',
      patientInstructions: this.patientInstructionsText(),
      problems: sectionTextsToProblems(notes.assessment ?? '', notes.plan ?? ''),
      keyFindings: this.bulletTextToLines(this.visitSummaryText()),
      treatmentPlanSummary: this.bulletTextToLines(this.treatmentPlanSummaryText()),
    };
    this.transcriptionsService.saveTranscriptionClinicalNotes(t.id, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.originalSoapNotes = {
            subjective: notes.subjective ?? '',
            objective: notes.objective ?? '',
            assessment: notes.assessment ?? '',
            plan: notes.plan ?? '',
          };
          this.originalVisitSummary = this.visitSummaryText();
          this.originalPatientInstructions = this.patientInstructionsText();
          this.originalTreatmentPlanSummary = this.treatmentPlanSummaryText();
          this.hasChanges = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Notes saved to the server.'
          });
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error saving clinical notes:', error);
          const status = error?.status;
          const detail =
            status === 404
              ? 'No analysis record found yet for this transcription. Try again after analysis completes.'
              : 'Could not save notes. Please try again.';
          this.messageService.add({
            severity: 'error',
            summary: 'Save failed',
            detail,
          });
        },
      });
  }

  /** Convert bullet-line textarea content back to string[] for the API. */
  private bulletTextToLines(text: string): string[] {
    if (!text || !text.trim()) {
      return [];
    }
    return text
      .split('\n')
      .map((line) => line.replace(/^\s*[•\-\*]\s*/, '').trim())
      .filter(Boolean);
  }

  onVisitSummaryInput(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    if (target) {
      this.visitSummaryText.set(target.value);
      this.checkForChanges();
      this.autoResizeTextarea(target);
      setTimeout(() => this.resizeAllSoapTextareas(), 0);
    }
  }

  onPatientInstructionsInput(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    if (target) {
      this.patientInstructionsText.set(target.value);
      this.checkForChanges();
      this.autoResizeTextarea(target);
      setTimeout(() => this.resizeAllSoapTextareas(), 0);
    }
  }

  onTreatmentPlanSummaryInput(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    if (target) {
      this.treatmentPlanSummaryText.set(target.value);
      this.checkForChanges();
      this.autoResizeTextarea(target);
      setTimeout(() => this.resizeAllSoapTextareas(), 0);
    }
  }



  onSoapNotesChange(field: keyof any, event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    if (target) {
      this.transcriptionDetailService.updateSoapNotes({ [field]: target.value });
      this.checkForChanges();
    }
  }

  onSoapNotesInput(field: keyof any, event: Event): void {
    this.onSoapNotesChange(field, event);
    const target = event.target as HTMLTextAreaElement;
    if (target) {
      this.autoResizeTextarea(target);
    }
    // In case formatting pipes adjust wrapping/line breaks, re-measure after render.
    setTimeout(() => this.resizeAllSoapTextareas(), 0);
  }

  onCopied(): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Copied',
      detail: 'Text copied to clipboard!'
    });
  }

  private formatSoapForDisplay(value: string | null | undefined, stripHeadingColon = false): string {
    if (!value) {
      return '';
    }

    const withBullets = value
      .replace(/\r/g, '')
      // Guardrail: only 1-2 digit prefixes (avoid years like "2018. ").
      .replace(/^\s*\d{1,2}\.\s+/gm, '• ');

    const lines = withBullets.split('\n');
    const out: string[] = [];

    for (let i = 0; i < lines.length; i++) {
      const raw = lines[i];
      const normalized = raw.trim() === '' ? '' : raw.trimStart();

      if (normalized.startsWith('• ')) {
        // Add a blank line before each new sub-category if not already separated.
        const last = out.length > 0 ? out[out.length - 1] : null;
        if (last !== null && last !== '') {
          out.push('');
        }

        let heading = normalized;
        if (stripHeadingColon) {
          heading = heading.replace(/:\s*$/, '');
        }

        out.push(heading);

        continue;
      }

      out.push(normalized);
    }

    return this.normalizeSoapText('other', out.join('\n'));
  }

  private formatAssessmentForDisplay(value: string | null | undefined): string {
    const base = this.normalizeSoapText('assessment', this.formatSoapForDisplay(value));
    // Mirror SoapSentenceBreaksPipe logic for "Copy All".
    const newlineCount = (base.match(/\n/g) ?? []).length;
    if (newlineCount >= 3 || base.length < 250) {
      return base;
    }

    const stopWords = new Set([
      'dr',
      'mr',
      'mrs',
      'ms',
      'prof',
      'sr',
      'jr',
      'vs',
      'etc',
      'eg',
      'ie',
      'no',
      'st',
      'mg',
      'ml',
      'kg',
      'lb',
      'oz',
      'hr',
      'min',
      'sec',
      'jan',
      'feb',
      'mar',
      'apr',
      'may',
      'jun',
      'jul',
      'aug',
      'sep',
      'sept',
      'oct',
      'nov',
      'dec',
      'am',
      'pm'
    ]);

    const paragraphStarters = new Set([
      'she',
      'he',
      'they',
      'patient',
      'the',
      'this',
      'overall',
      'additionally',
      'also',
      'however',
      'moreover',
      'furthermore',
      'in',
      'on',
      'at',
      'today',
      'currently'
    ]);

    const isUpperShortToken = (token: string) =>
      token.length <= 2 && token.toUpperCase() === token && /[A-Z]/.test(token);

    const prevToken = (s: string, endIdxInclusive: number): string => {
      let i = endIdxInclusive;
      while (i >= 0 && !/[A-Za-z0-9]/.test(s[i])) {
        i--;
      }
      let end = i;
      while (i >= 0 && /[A-Za-z0-9]/.test(s[i])) {
        i--;
      }
      return s.slice(i + 1, end + 1);
    };

    const nextWordLower = (s: string, startIdx: number): string => {
      let i = startIdx;
      while (i < s.length && /[^A-Za-z]/.test(s[i])) {
        i++;
      }
      const start = i;
      while (i < s.length && /[A-Za-z]/.test(s[i])) {
        i++;
      }
      return s.slice(start, i).toLowerCase();
    };

    let out = '';
    const n = base.length;
    for (let i = 0; i < n; i++) {
      const ch = base[i];
      out += ch;

      if (ch !== '.' && ch !== '?' && ch !== '!') {
        continue;
      }

      const prevChar = i > 0 ? base[i - 1] : '';
      const nextChar = i + 1 < n ? base[i + 1] : '';
      if (/\d/.test(prevChar) && /\d/.test(nextChar)) {
        continue;
      }

      let j = i + 1;
      let sawSpace = false;
      while (j < n && base[j] === ' ') {
        sawSpace = true;
        j++;
      }
      if (!sawSpace || j >= n) {
        continue;
      }
      const nextNonSpace = base[j];
      if (!/[A-Z]/.test(nextNonSpace)) {
        continue;
      }

      const token = prevToken(base, i - 1);
      const tokenLower = token.toLowerCase();
      if (stopWords.has(tokenLower) || isUpperShortToken(token)) {
        continue;
      }

      const w = nextWordLower(base, j);
      out += paragraphStarters.has(w) ? '\n\n' : '\n';
      i = j - 1;
    }

    return out;
  }

  private normalizeSoapText(section: 'subjective' | 'objective' | 'assessment' | 'plan' | 'other', value: string): string {
    if (!value) {
      return '';
    }

    let text = value.replace(/\r/g, '');

    if (section === 'assessment') {
      text = this.stripProblemNumberPrefixes(text);
    }

    text = this.compactHeaderToFirstContentLine(text);
    return text;
  }

  private stripProblemNumberPrefixes(text: string): string {
    const lines = text.split('\n');
    const out: string[] = [];

    for (const raw of lines) {
      const m = (raw ?? '').match(/^(\s*)(•\s+)?(.*)$/);
      const indent = m?.[1] ?? '';
      const bullet = m?.[2] ?? '';
      let rest = (m?.[3] ?? '').trimStart();

      rest = rest.replace(/^problem\s+\d+\s*[:\-–—]\s*/iu, '');
      rest = rest.replace(/^problem\s+\d+\s+(?=\S)/iu, '');

      out.push(`${indent}${bullet}${rest}`);
    }

    return out.join('\n');
  }

  private compactHeaderToFirstContentLine(text: string): string {
    const lines = text.split('\n');
    const out: string[] = [];

    const isBlank = (s: string) => s.trim() === '';
    const isHeadingLine = (line: string): boolean => {
      const t = (line ?? '').trim();
      if (!t) return false;

      const withoutBullet = t.startsWith('• ') ? t.slice(2).trimStart() : t;
      const withoutNumber = withoutBullet.replace(/^\d{1,2}\.\s+/, '');
      const h = withoutNumber.trim();

      if (/[.?!]$/.test(h)) return false;
      if (h.length > 90) return false;
      if (!/[A-Za-z]/.test(h)) return false;
      if (/^[-*]\s+/.test(h)) return false;

      const wordCount = h.split(/\s+/).filter(Boolean).length;
      return wordCount >= 1 && wordCount <= 12;
    };

    for (let i = 0; i < lines.length; i++) {
      const current = lines[i] ?? '';

      if (isHeadingLine(current)) {
        out.push(current.trimEnd());

        let j = i + 1;
        while (j < lines.length && isBlank(lines[j] ?? '')) {
          j++;
        }

        if (j > i + 1 && j < lines.length) {
          const next = lines[j] ?? '';
          const nextTrimmed = next.trim();
          const indented = /^\s+/.test(next) ? nextTrimmed : `  ${nextTrimmed}`;
          out.push(indented);
          i = j;
          continue;
        }

        continue;
      }

      out.push(current);
    }

    return out.join('\n');
  }

  private autoResizeTextarea(textarea: HTMLTextAreaElement): void {
    // Reset then grow to content height.
    textarea.style.height = 'auto';
    textarea.style.overflowY = 'hidden';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }

  private resizeAllSoapTextareas(): void {
    if (!this.soapTextareas) {
      return;
    }
    this.soapTextareas.forEach(ref => this.autoResizeTextarea(ref.nativeElement));
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    // Wrapping changes with width; remeasure heights.
    this.resizeAllSoapTextareas();
  }

  copyAllSoapNotes(): void {
    const currentNotes = this.soapNotes();
    
    // Create formatted text with section names as separators
    const allText = [
      '',
      'SUBJECTIVE:',
      this.formatSoapForDisplay(currentNotes.subjective),
      '',
      'OBJECTIVE:',
      this.formatSoapForDisplay(currentNotes.objective),
      '',
      'ASSESSMENT:',
      this.formatAssessmentForDisplay(currentNotes.assessment),
      '',
      'PLAN:',
      this.formatSoapForDisplay(currentNotes.plan),
      '',
      'PATIENT INSTRUCTIONS:',
      this.patientInstructionsText(),
      '',
      'TREATMENT PLAN SUMMARY:',
      this.treatmentPlanSummaryText(),
      '',
      'ICD-10 CODES:',
      this.icdCodesText()
    ].join('\n').trim();
    
    if (allText && allText.length > 0) {
      navigator.clipboard.writeText(allText).then(() => {
        this.messageService.add({
          severity: 'success',
          summary: 'Copied All',
          detail: 'All SOAP Notes copied to clipboard!'
        });
      }).catch(() => {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = allText;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        
        this.messageService.add({
          severity: 'success',
          summary: 'Copied All',
          detail: 'All SOAP Notes copied to clipboard!'
        });
      });
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Content',
        detail: 'No SOAP Notes content to copy'
      });
    }
  }

  copyTranscriptText(target?: 'translated' | 'original'): void {
    let textToCopy = this.fullTranscriptText() || '';
    if (this.hasTranslatedTranscript()) {
      if (target === 'translated') {
        textToCopy = this.translatedTranscriptText() || '';
      } else if (target === 'original') {
        textToCopy = this.originalTranscriptText() || '';
      } else if (this.activeTranscriptTab() === 'translated') {
        textToCopy = this.translatedTranscriptText() || '';
      } else {
        textToCopy = this.originalTranscriptText() || '';
      }
    }

    if (!textToCopy) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Content',
        detail: 'No transcript content to copy'
      });
      return;
    }

    navigator.clipboard.writeText(textToCopy).then(() => {
      this.messageService.add({
        severity: 'success',
        summary: 'Copied',
        detail: 'Transcript copied to clipboard!'
      });
    }).catch(() => {
      const textArea = document.createElement('textarea');
      textArea.value = textToCopy;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);

      this.messageService.add({
        severity: 'success',
        summary: 'Copied',
        detail: 'Transcript copied to clipboard!'
      });
    });
  }

  onPrint(): void {
    // Convert textareas to divs for print to show full content
    const textareas = document.querySelectorAll('.soap-textarea') as NodeListOf<HTMLTextAreaElement>;
    const originalElements: { element: HTMLTextAreaElement; parent: HTMLElement; nextSibling: Node | null }[] = [];
    
    textareas.forEach((textarea) => {
      // Store original element info
      const parent = textarea.parentElement!;
      const nextSibling = textarea.nextSibling;
      
      // Create a div to replace the textarea
      const div = document.createElement('div');
      div.className = 'soap-textarea-print';
      div.textContent = textarea.value;
      div.style.cssText = `
        width: 100%;
        min-height: auto;
        max-height: none;
        overflow: visible;
        white-space: pre-wrap;
        word-wrap: break-word;
        border: none;
        background: white;
        color: black;
        padding: 5px;
        margin-bottom: 10px;
        font-family: "Inter", sans-serif;
        font-size: 14px;
        line-height: 1.5;
        box-sizing: border-box;
      `;
      
      // Replace textarea with div
      parent.insertBefore(div, textarea);
      textarea.style.display = 'none';
      textarea.style.visibility = 'hidden';
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      
      // Store for restoration
      originalElements.push({ element: textarea, parent, nextSibling });
    });
    
    // Print
    window.print();
    
    // Restore textareas after printing
    setTimeout(() => {
      originalElements.forEach(({ element, parent, nextSibling }) => {
        // Remove the print div
        const printDiv = parent.querySelector('.soap-textarea-print');
        if (printDiv) {
          parent.removeChild(printDiv);
        }
        
        // Show the original textarea
        element.style.display = '';
        element.style.visibility = '';
        element.style.position = '';
        element.style.left = '';
      });
    }, 50);
  }

  private checkForChanges(): void {
    const currentNotes = this.soapNotes();
    const originalNotes = this.originalSoapNotes;

    let soapChanged = false;
    if (originalNotes) {
      soapChanged = (['subjective', 'objective', 'assessment', 'plan'] as const).some((key) => {
        const currentValue = currentNotes[key];
        const originalValue = originalNotes[key];
        return currentValue !== originalValue;
      });
    }

    const visitChanged = this.visitSummaryText() !== this.originalVisitSummary;
    const patientChanged = this.patientInstructionsText() !== this.originalPatientInstructions;
    const treatmentChanged = this.treatmentPlanSummaryText() !== this.originalTreatmentPlanSummary;

    this.hasChanges = soapChanged || visitChanged || patientChanged || treatmentChanged;
  }

  /**
   * Re-analyze the current transcription with updated medical analysis
   */
  onReanalyze(): void {
    const transcription = this.transcription();
    if (!transcription) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No transcription found to re-analyze'
      });
      return;
    }

    this.isReanalyzing.set(true);
    
    this.transcriptionsService.reanalyzeTranscription(transcription.id, transcription.specialty)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isReanalyzing.set(false);
          
          // Show success message
          this.messageService.add({
            severity: 'success',
            summary: 'Re-analysis Started',
            detail: 'Your transcription is being re-analyzed. You will be redirected to the transcriptions list.'
          });
          
          // Redirect to transcriptions list after a short delay
          setTimeout(() => {
            this.router.navigate(['/v0/transcriptions']);
          }, 1000);
        },
        error: (error) => {
          this.isReanalyzing.set(false);
          console.error('Error re-analyzing transcription:', error);
          
          const summary = error?.error?.summary ?? error?.error?.message;
          this.messageService.add({
            severity: 'error',
            summary: 'Re-analysis Failed',
            detail:
              typeof summary === 'string' && summary.trim()
                ? summary.trim()
                : 'Failed to re-analyze transcription. Please try again.'
          });
        }
      });
  }

  /**
   * Format plan text for textarea display by adding line breaks between numbered items
   * @param planText The plan text from the backend
   * @returns Formatted plan text with line breaks
   */
  private formatPlanForTextarea(planText: string): string {
    if (!planText) return '';
    
    // Split by numbered items (1., 2., 3., etc.) and add line breaks
    return planText
      .replace(/(\d+\.\s)/g, '\n$1') // Add newline before each numbered item
      .replace(/^\n/, '') // Remove leading newline
      .trim();
  }

  ngOnDestroy(): void {
    this.clearDisplayedClinicalContent();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
